/**
 * FlightAreaLayer — the mission's single flight area on the map (mission-map unification, PR6).
 *
 * Renders the area (Polygon, LineString + its corridor, or a circle — a Point centre + `circleRadiusM`)
 * and, when `editable`, the Leaflet Draw toolbar to draw / edit / delete it. The parent owns the
 * geometry (controlled component): a finished drawing or edit is reported through
 * `onGeometryChange(geometry, { source: 'created' | 'edited' })` and comes back in through `geometry`;
 * a circle's radius goes the same way through `onCircleRadiusChange` / `circleRadiusM`.
 *
 * leaflet-draw is driven directly (not through react-leaflet-draw's EditControl, whose event handlers
 * are re-registered without being removed whenever a callback prop changes): one L.Control.Draw per
 * mount, callbacks read through a ref.
 *
 * While a draw / edit / delete session is open the toolbar owns the layers: `onDrawActiveChange`
 * reports it so the parent can block every other change of the area (import, delete, the mission's
 * main save). A geometry that still arrives from outside during a session is never overwritten by the
 * session's layer — its edit/delete result is dropped — and the layer group is re-synced from the
 * controlled geometry once the session stops.
 *
 * Editing a route's vertices adds a "corridor width" action next to Save / Cancel in the toolbar's own
 * action bar (`onCorridorWidthRequest` opens the parent's width popup at the line). The action is added
 * on this control's edit toolbar instance only (no prototype patch). Cancel reverts both the shape and
 * the corridor width to the state the session started with; Save confirms both.
 *
 * A circle (desktop / mouse only) is drawn by a click (the default radius from the settings) or by a
 * drag from the centre, which stops at the maximum radius; the tool is offered only once the settings
 * with the radius limits are loaded. While a circle's vertex edit is open the radius lives in a draft
 * (`circleRadiusDraft`, see CIRCLE_DRAFT_IDLE): dragging the radius handle reports it with origin
 * 'layer' and is never written back to the circle, a number typed into the radius popup or the panel
 * comes back with origin 'input' and is applied to the circle (marked edited, so Save keeps it). Save
 * with an invalid draft saves nothing and opens the radius popup; Cancel reverts the circle and drops
 * the draft. `onCircleSessionChange` reports drawing or editing a circle (the parent makes the take-off
 * marker passive so the circle's handles can be grabbed). The handles, the drawing and the Save action
 * are adapted on this control's and this layer's instances only (no prototype patch).
 *
 * A legacy area the editor cannot represent (Multi*, FeatureCollection, polygon with holes) is drawn
 * read-only as a grey dashed outline outside the editable group — it can be replaced by a new drawing,
 * never edited. A circle without a usable radius shows only its centre until a radius is entered.
 */
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  classifyFlightArea, FLIGHT_AREA_KIND, corridorWeightPx, getCircleCenter, parseCircleRadius, getCircleRadiusIssue,
  clampCircleRadius, CIRCLE_CLICK_DRAG_THRESHOLD_PX, CIRCLE_DRAFT_IDLE,
} from '../../lib/flightArea';

export const FLIGHT_AREA_STYLE = {
  polygon: { color: '#2563EB', weight: 2, fillColor: '#3B82F6', fillOpacity: 0.2 },
  circle: { color: '#2563EB', weight: 2, fillColor: '#3B82F6', fillOpacity: 0.2 },
  line: { color: '#1D4ED8', weight: 3, dashArray: '8 6' },
  corridor: { color: '#3B82F6', opacity: 0.3 },
  invalid: { color: '#6B7280', weight: 2, dashArray: '4 4', fillColor: '#9CA3AF', fillOpacity: 0.1 },
};

const toLatLngs = (positions) => positions.map(([lng, lat]) => [lat, lng]);

/**
 * The radius handle of a circle's vertex edit: leaflet-draw 1.0.4's L.Edit.Circle._resize with the
 * radius rounded to whole metres within the limits (so the circle shows exactly the value the popup
 * shows and Save stores), the radius in the edit tooltip, and without its undeclared global `radius`.
 * The handle sits on the circle straight below the centre (leaflet-draw puts it up and to the right,
 * under the radius popup that opens above the centre). Set on this layer's handler only.
 */
const installRadiusHandle = (layer, getLimits) => {
  // Due south along the meridian: exactly the radius away, also right after a move (the projected
  // pixel radius leaflet-draw uses is still the old centre's then).
  layer.editing._getResizeMarkerPoint = function radiusHandleBelow(latlng) {
    const center = L.latLng(latlng);
    return L.latLng(center.lat - (this._shape.getRadius() / L.CRS.Earth.R) * (180 / Math.PI), center.lng);
  };
  layer.editing._resize = function resizeWithinLimits(latlng) {
    const radius = clampCircleRadius(this._map.distance(this._moveMarker.getLatLng(), latlng), getLimits());
    if (this._map._editTooltip) {
      this._map._editTooltip.updateContent({
        text: `${L.drawLocal.edit.handlers.edit.tooltip.subtext}<br />${L.drawLocal.edit.handlers.edit.tooltip.text}`,
        subtext: `${L.drawLocal.draw.handlers.circle.radius}: ${L.GeometryUtil.readableDistance(radius, true)}`,
      });
    }
    this._shape.setRadius(radius);
    this._map.fire(L.Draw.Event.EDITRESIZE, { layer: this._shape });
  };
};

/**
 * Puts the radius handle back below the centre after a drag of any of the circle's handles. Only after
 * a drag — moving the handle during one would end that drag. The same marker, so it stays draggable.
 */
const resetRadiusHandle = (layer) => {
  const handle = layer.editing?._resizeMarkers?.[0];
  if (handle) handle.setLatLng(layer.editing._getResizeMarkerPoint(layer.getLatLng()));
};

/**
 * Drawing a circle: a click, or a drag shorter than CIRCLE_CLICK_DRAG_THRESHOLD_PX, gives the default
 * radius; a longer drag stops at the maximum. Wraps the handler of this control only.
 */
const installCircleDrawing = (handler, map, getLimits) => {
  const baseMouseDown = handler._onMouseDown;
  const baseDrawShape = handler._drawShape;
  const baseMouseUp = handler._onMouseUp;
  handler._onMouseDown = function onMouseDown(e) {
    this._lastLatLng = null;
    baseMouseDown.call(this, e);
  };
  handler._drawShape = function drawShape(latlng) {
    this._lastLatLng = latlng;
    baseDrawShape.call(this, latlng);
    const max = getLimits()?.max;
    if (max && this._shape.getRadius() > max) this._shape.setRadius(max);
  };
  handler._onMouseUp = function onMouseUp(e) {
    const limits = getLimits();
    if (this._startLatLng && limits) {
      const px = this._lastLatLng
        ? map.latLngToContainerPoint(this._startLatLng).distanceTo(map.latLngToContainerPoint(this._lastLatLng))
        : 0;
      if (px < CIRCLE_CLICK_DRAG_THRESHOLD_PX) {
        if (this._shape) {
          this._shape.setRadius(limits.default);
        } else {
          this._shape = new L.Circle(this._startLatLng, limits.default, this.options.shapeOptions);
          this._map.addLayer(this._shape);
        }
      }
    }
    baseMouseUp.call(this, e);
  };
};

const createAreaLayer = (geometry, circleRadiusM) => {
  const kind = classifyFlightArea(geometry).kind;
  if (kind === FLIGHT_AREA_KIND.CIRCLE) {
    const radius = parseCircleRadius(circleRadiusM);
    if (!(radius > 0)) return null;
    const { lat, lng } = getCircleCenter(geometry);
    return L.circle([lat, lng], { radius, ...FLIGHT_AREA_STYLE.circle });
  }
  if (kind === FLIGHT_AREA_KIND.POLYGON) {
    const ring = toLatLngs(geometry.coordinates[0]);
    // Leaflet closes the ring itself — drop the GeoJSON closing duplicate so editing has no twin vertex.
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (ring.length > 1 && first[0] === last[0] && first[1] === last[1]) ring.pop();
    // `poly` = leaflet-draw's per-layer vertex-edit options: a vertex drag that would make the edges
    // cross is reverted (the server rejects a self-intersecting polygon). Set on the polygon layer
    // only, not through the control's `edit.poly`, which would apply to a route too — a LineString may
    // cross itself (a looping route is legitimate, the server accepts it).
    const layer = L.polygon(ring, {
      ...FLIGHT_AREA_STYLE.polygon,
      poly: { allowIntersection: false, drawError: { color: '#DC2626' } },
    });
    // A reverted drag flashes the error colour (leaflet-draw 1.0.4, PolyVerticesEdit._onMarkerDrag): right
    // after intersects() it reads options.color as the colour to restore 1 s later, then sets the error
    // colour. A drag that crosses again within that second reads the error colour itself, so the polygon
    // would stay red — on a crossing, put the normal colour back before it is read.
    const intersects = layer.intersects;
    layer.intersects = function intersectsRestoringColor() {
      const crossed = intersects.call(this);
      if (crossed) this.options.color = FLIGHT_AREA_STYLE.polygon.color;
      return crossed;
    };
    return layer;
  }
  if (kind === FLIGHT_AREA_KIND.LINE) {
    return L.polyline(toLatLngs(geometry.coordinates), { ...FLIGHT_AREA_STYLE.line });
  }
  return null;
};

// Leaflet Draw's toolbar/tooltip texts are a global object (L.drawLocal) — set before each control is
// created so the toolbar follows the app language.
const DRAW_TEXT = {
  cs: {
    polygon: 'Nakreslit oblast (polygon)', polyline: 'Nakreslit trasu (linie s koridorem)',
    cancel: 'Zrušit', cancelTitle: 'Zrušit kreslení', finish: 'Dokončit', finishTitle: 'Dokončit kreslení',
    undo: 'Smazat poslední bod', undoTitle: 'Smazat poslední nakreslený bod',
    polygonStart: 'Klikněte pro první vrchol oblasti.', polygonCont: 'Klikněte pro další vrchol.',
    polygonEnd: 'Klikněte na první vrchol pro uzavření oblasti.',
    lineStart: 'Klikněte pro začátek trasy.', lineCont: 'Klikněte pro další bod trasy.',
    lineEnd: 'Klikněte na poslední bod pro dokončení trasy.',
    intersect: 'Hrany oblasti se nesmí křížit.',
    edit: 'Upravit vrcholy', editDisabled: 'Není co upravit', remove: 'Smazat oblast', removeDisabled: 'Není co smazat',
    save: 'Uložit', saveTitle: 'Potvrdit změny', clearAll: 'Smazat vše', clearAllTitle: 'Smazat oblast',
    editTooltip: 'Táhněte vrcholy pro úpravu tvaru.', editSubtext: 'Zrušením vrátíte změny.',
    removeTooltip: 'Klikněte na oblast pro smazání.',
    circle: 'Nakreslit kružnici (střed a poloměr)',
    circleStart: 'Klikněte do středu (výchozí poloměr), nebo táhněte od středu pro poloměr.',
    circleRadius: 'Poloměr', shapeEnd: 'Pusťte myš pro dokončení.',
  },
  en: {
    polygon: 'Draw area (polygon)', polyline: 'Draw route (line with corridor)',
    cancel: 'Cancel', cancelTitle: 'Cancel drawing', finish: 'Finish', finishTitle: 'Finish drawing',
    undo: 'Delete last point', undoTitle: 'Delete the last drawn point',
    polygonStart: 'Click to place the first vertex of the area.', polygonCont: 'Click to continue drawing.',
    polygonEnd: 'Click the first vertex to close the area.',
    lineStart: 'Click to start the route.', lineCont: 'Click to continue the route.',
    lineEnd: 'Click the last point to finish the route.',
    intersect: 'The area edges must not cross.',
    edit: 'Edit vertices', editDisabled: 'Nothing to edit', remove: 'Delete area', removeDisabled: 'Nothing to delete',
    save: 'Save', saveTitle: 'Confirm changes', clearAll: 'Clear all', clearAllTitle: 'Delete the area',
    editTooltip: 'Drag the vertices to change the shape.', editSubtext: 'Cancel to undo the changes.',
    removeTooltip: 'Click the area to delete it.',
    circle: 'Draw circle (centre and radius)',
    circleStart: 'Click the centre (default radius) or drag from the centre to set the radius.',
    circleRadius: 'Radius', shapeEnd: 'Release mouse to finish.',
  },
};

const applyDrawLocale = (language) => {
  const s = DRAW_TEXT[language] || DRAW_TEXT.en;
  const d = L.drawLocal;
  d.draw.toolbar.buttons.polygon = s.polygon;
  d.draw.toolbar.buttons.polyline = s.polyline;
  d.draw.toolbar.buttons.circle = s.circle;
  d.draw.toolbar.actions = { title: s.cancelTitle, text: s.cancel };
  d.draw.toolbar.finish = { title: s.finishTitle, text: s.finish };
  d.draw.toolbar.undo = { title: s.undoTitle, text: s.undo };
  d.draw.handlers.polygon.tooltip = { start: s.polygonStart, cont: s.polygonCont, end: s.polygonEnd };
  d.draw.handlers.polyline.tooltip = { start: s.lineStart, cont: s.lineCont, end: s.lineEnd };
  d.draw.handlers.polyline.error = `<strong>${s.intersect}</strong>`;
  d.draw.handlers.circle.tooltip = { start: s.circleStart };
  d.draw.handlers.circle.radius = s.circleRadius;
  d.draw.handlers.simpleshape.tooltip = { end: s.shapeEnd };
  d.edit.toolbar.actions.save = { title: s.saveTitle, text: s.save };
  d.edit.toolbar.actions.cancel = { title: s.cancelTitle, text: s.cancel };
  d.edit.toolbar.actions.clearAll = { title: s.clearAllTitle, text: s.clearAll };
  d.edit.toolbar.buttons = { edit: s.edit, editDisabled: s.editDisabled, remove: s.remove, removeDisabled: s.removeDisabled };
  d.edit.handlers.edit.tooltip = { text: s.editTooltip, subtext: s.editSubtext };
  d.edit.handlers.remove.tooltip = { text: s.removeTooltip };
};

/** Thick round-capped stroke = the corridor buffer in map scale; re-sized on every zoom. */
const CorridorLayer = ({ geometry, widthM }) => {
  const map = useMap();
  useEffect(() => {
    if (classifyFlightArea(geometry).kind !== FLIGHT_AREA_KIND.LINE || !(widthM > 0)) return undefined;
    const latlngs = toLatLngs(geometry.coordinates);
    const lats = latlngs.map((p) => p[0]);
    const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const layer = L.polyline(latlngs, {
      ...FLIGHT_AREA_STYLE.corridor,
      weight: corridorWeightPx(widthM, map.getZoom(), midLat),
      lineCap: 'round',
      lineJoin: 'round',
      interactive: false,
      // Clipping the centreline at the viewport edge would end the corridor with a visible round cap.
      noClip: true,
    }).addTo(map);
    layer.bringToBack();
    const onZoom = () => layer.setStyle({ weight: corridorWeightPx(widthM, map.getZoom(), midLat) });
    map.on('zoomend', onZoom);
    return () => {
      map.off('zoomend', onZoom);
      map.removeLayer(layer);
    };
  }, [map, geometry, widthM]);
  return null;
};

/** Read-only outline of a stored geometry the editor cannot take over (legacy data). */
const InvalidAreaLayer = ({ geometry }) => {
  const map = useMap();
  useEffect(() => {
    if (!geometry) return undefined;
    let layer;
    try {
      layer = L.geoJSON(geometry, {
        style: () => ({ ...FLIGHT_AREA_STYLE.invalid }),
        pointToLayer: (_feature, latlng) => L.circleMarker(latlng, { ...FLIGHT_AREA_STYLE.invalid, radius: 6 }),
        interactive: false,
      }).addTo(map);
    } catch (err) {
      console.warn('Stored flight area cannot be drawn:', err);
      return undefined;
    }
    return () => map.removeLayer(layer);
  }, [map, geometry]);
  return null;
};

/** The centre of a circle without a usable radius (there is nothing to draw around it yet). */
const CircleCenterLayer = ({ geometry }) => {
  const map = useMap();
  useEffect(() => {
    const center = getCircleCenter(geometry);
    if (!center) return undefined;
    const layer = L.circleMarker([center.lat, center.lng], {
      ...FLIGHT_AREA_STYLE.circle, radius: 5, fillOpacity: 0.6, interactive: false,
    }).addTo(map);
    return () => map.removeLayer(layer);
  }, [map, geometry]);
  return null;
};

export const FlightAreaLayer = ({
  geometry,
  corridorWidthM,
  circleRadiusM,
  circleRadiusDraft = CIRCLE_DRAFT_IDLE,
  circleRadiusLimits = null,
  editable = false,
  onGeometryChange,
  onDrawActiveChange,
  onLineCreated,
  onCorridorWidthChange,
  onCorridorWidthRequest,
  corridorWidthActionText,
  corridorWidthActionTitle,
  onCircleCreated,
  onCircleRadiusChange,
  onCircleRadiusDraft,
  onCircleRadiusRequest,
  onCircleSessionChange,
  circleRadiusActionText,
  circleRadiusActionTitle,
  confirmReplaceText,
  language = 'cs',
}) => {
  const map = useMap();
  const groupRef = useRef(null);
  const busyRef = useRef(false);
  // The controlled geometry an edit/delete session started from (see onEdited/onDeleted).
  const sessionGeometryRef = useRef(null);
  // The open vertex-edit session: corridor width at its start (Cancel restores it), whether it was
  // saved, whether the width popup was opened from it (closed again when the session ends), and for a
  // circle `circle` = { layer, startRadiusM, startCenter, dragging, radiusRequested } (else null).
  const editSessionRef = useRef(null);
  // Whether a circle is being drawn or edited, as last reported through onCircleSessionChange.
  const circleSessionRef = useRef(false);
  // Bumped when a session stops, so a geometry change skipped during it is applied.
  const [syncTick, setSyncTick] = useState(0);
  const [preview, setPreview] = useState(null);
  const latest = useRef({});
  latest.current = {
    geometry, corridorWidthM, circleRadiusDraft, circleRadiusLimits, onGeometryChange, onDrawActiveChange,
    onLineCreated, onCorridorWidthChange, onCorridorWidthRequest, corridorWidthActionText, corridorWidthActionTitle,
    onCircleCreated, onCircleRadiusChange, onCircleRadiusDraft, onCircleRadiusRequest, onCircleSessionChange,
    circleRadiusActionText, circleRadiusActionTitle, confirmReplaceText,
  };

  const kind = classifyFlightArea(geometry).kind;
  const editableGeometry = kind === FLIGHT_AREA_KIND.POLYGON || kind === FLIGHT_AREA_KIND.LINE
    || kind === FLIGHT_AREA_KIND.CIRCLE ? geometry : null;
  const layerRadiusM = kind === FLIGHT_AREA_KIND.CIRCLE ? parseCircleRadius(circleRadiusM) : null;
  const hasCircleTool = Boolean(circleRadiusLimits);

  // The (editable) area layer lives in one feature group for the lifetime of the map.
  useEffect(() => {
    const group = L.featureGroup().addTo(map);
    groupRef.current = group;
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  // Sync the controlled geometry (and a circle's radius) into the group — never in the middle of a
  // Leaflet Draw edit session, which holds references to the layers it is editing; a change skipped
  // then is applied when the session stops (syncTick).
  useEffect(() => {
    const group = groupRef.current;
    if (!group || busyRef.current) return;
    group.clearLayers();
    const layer = createAreaLayer(editableGeometry, layerRadiusM);
    if (layer instanceof L.Circle) installRadiusHandle(layer, () => latest.current.circleRadiusLimits);
    if (layer) group.addLayer(layer);
  }, [editableGeometry, layerRadiusM, syncTick]);

  // A radius typed into the popup or the panel during a circle's vertex edit is applied to the circle —
  // never a draft that came from the circle itself (dragging the radius handle: moving the handles now
  // would end that drag), never while a handle is dragged, never an invalid one (Save is blocked then).
  // `edited` is the flag leaflet-draw's save() reads; a programmatic setRadius alone would not be saved.
  useEffect(() => {
    const circle = editSessionRef.current?.circle;
    if (!circle || !circleRadiusDraft?.active || circleRadiusDraft.origin !== 'input' || circle.dragging) return;
    if (getCircleRadiusIssue(circleRadiusDraft.value, circleRadiusLimits) !== null) return;
    const radius = parseCircleRadius(circleRadiusDraft.value);
    if (radius === circle.layer.getRadius()) return;
    circle.layer.setRadius(radius);
    if (circle.layer.editing?.enabled()) circle.layer.editing.updateMarkers();
    circle.layer.edited = true;
  }, [circleRadiusDraft, circleRadiusLimits]);

  useEffect(() => {
    if (!editable || !groupRef.current) return undefined;
    applyDrawLocale(language);
    const s = DRAW_TEXT[language] || DRAW_TEXT.en;
    const getLimits = () => latest.current.circleRadiusLimits;
    const control = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: false,
          metric: true,
          drawError: { color: '#DC2626', message: `<strong>${s.intersect}</strong>` },
          shapeOptions: { ...FLIGHT_AREA_STYLE.polygon },
        },
        polyline: { metric: true, shapeOptions: { ...FLIGHT_AREA_STYLE.line } },
        rectangle: false,
        // Only with the radius limits from the settings: a click needs the default radius, a drag the maximum.
        circle: hasCircleTool ? { shapeOptions: { ...FLIGHT_AREA_STYLE.circle }, showRadius: true, metric: true } : false,
        circlemarker: false,
        marker: false,
      },
      edit: { featureGroup: groupRef.current, remove: true },
    });
    map.addControl(control);
    const circleHandler = control._toolbars?.draw?._modes?.circle?.handler;
    if (circleHandler) installCircleDrawing(circleHandler, map, getLimits);

    // "Corridor width" / "Radius" in the action bar of a route's / circle's vertex edit, and Save
    // guarded against an invalid radius draft. getActions is overridden on this toolbar instance only;
    // it is read each time an edit mode is activated.
    const editToolbar = control._toolbars?.edit;
    const requestWidth = () => {
      const { onCorridorWidthRequest: request } = latest.current;
      const layer = groupRef.current?.getLayers()[0];
      const latlngs = layer?.getLatLngs?.();
      if (!request || !latlngs?.length) return;
      if (editSessionRef.current) editSessionRef.current.widthRequested = true;
      const { lat, lng } = latlngs[latlngs.length - 1];
      request({ lat, lng });
    };
    const requestRadius = () => {
      const { onCircleRadiusRequest: request } = latest.current;
      const layer = groupRef.current?.getLayers()[0];
      if (!request || !(layer instanceof L.Circle)) return;
      const circle = editSessionRef.current?.circle;
      if (circle) circle.radiusRequested = true;
      const { lat, lng } = layer.getLatLng();
      request({ lat, lng });
    };
    // Save of a circle's vertex edit with an invalid radius draft saves nothing: the session stays open
    // and the radius popup shows the problem. Cancel always works.
    const saveUnlessRadiusInvalid = () => {
      const circle = editSessionRef.current?.circle;
      const { circleRadiusDraft: draft, circleRadiusLimits: limits } = latest.current;
      if (circle && draft?.active && getCircleRadiusIssue(draft.value, limits) !== null) {
        requestRadius();
        return;
      }
      editToolbar._save();
    };
    if (editToolbar) {
      const baseGetActions = editToolbar.getActions;
      editToolbar.getActions = function getActions(handler) {
        const actions = baseGetActions.call(this, handler);
        if (handler?.type !== L.EditToolbar.Edit.TYPE) return actions;
        const save = actions.find((a) => a.callback === this._save);
        if (save) save.callback = saveUnlessRadiusInvalid;
        const {
          geometry: current, onCorridorWidthRequest: widthRequest, corridorWidthActionText: widthText,
          corridorWidthActionTitle: widthTitle, onCircleRadiusRequest: radiusRequest,
          circleRadiusActionText: radiusText, circleRadiusActionTitle: radiusTitle,
        } = latest.current;
        const currentKind = classifyFlightArea(current).kind;
        if (widthRequest && widthText && currentKind === FLIGHT_AREA_KIND.LINE) {
          actions.push({ title: widthTitle || widthText, text: widthText, callback: requestWidth, context: this });
        }
        if (radiusRequest && radiusText && currentKind === FLIGHT_AREA_KIND.CIRCLE) {
          actions.push({ title: radiusTitle || radiusText, text: radiusText, callback: requestRadius, context: this });
        }
        return actions;
      };
    }

    const setBusy = (busy) => {
      busyRef.current = busy;
      latest.current.onDrawActiveChange?.(busy);
    };
    const setCircleSession = (active) => {
      if (circleSessionRef.current === active) return;
      circleSessionRef.current = active;
      latest.current.onCircleSessionChange?.(active);
    };
    const emit = (layer, source) => {
      const geom = layer?.toGeoJSON()?.geometry || null;
      latest.current.onGeometryChange?.(geom, { source });
      return geom;
    };

    const onDrawStart = (e) => {
      const { geometry: current, confirmReplaceText: text } = latest.current;
      if (current && text && !window.confirm(text)) {
        // Leave the toolbar's own enable() call stack before disabling the mode again.
        setTimeout(() => control._toolbars?.draw?.disable?.(), 0);
        return;
      }
      setBusy(true);
      if (e?.layerType === 'circle') setCircleSession(true);
    };
    const onCreated = (e) => {
      if (e.layerType === 'circle') {
        // A circle's GeoJSON is its centre only — the radius goes with it, in whole metres within the limits.
        emit(e.layer, 'created');
        latest.current.onCircleRadiusChange?.(clampCircleRadius(e.layer.getRadius(), getLimits()));
        const { lat, lng } = e.layer.getLatLng();
        latest.current.onCircleCreated?.({ lat, lng });
        return;
      }
      const geom = emit(e.layer, 'created');
      if (e.layerType === 'polyline' && geom?.coordinates?.length) {
        const [lng, lat] = geom.coordinates[geom.coordinates.length - 1];
        latest.current.onLineCreated?.({ lat, lng });
      }
    };
    // The area was replaced from outside after the session started: the session's layer is stale and
    // must not overwrite the newer geometry (the re-sync on stop shows the current one).
    const replacedDuringSession = () => latest.current.geometry !== sessionGeometryRef.current;
    const onEdited = (e) => {
      const session = editSessionRef.current;
      if (session) session.saved = true;
      if (replacedDuringSession()) return;
      let last = null;
      e.layers.eachLayer((layer) => { last = layer; });
      if (!last) return;
      if (last instanceof L.Circle) {
        // The radius as it is: the stored value when only the centre moved, the typed number, or the
        // whole metres of a drag. A Save that changed nothing (a number typed back to the start value)
        // reports nothing, like a polygon saved without a drag.
        const radius = last.getRadius();
        const circle = session?.circle;
        if (circle && radius === circle.startRadiusM && last.getLatLng().equals(circle.startCenter)) return;
        emit(last, 'edited');
        latest.current.onCircleRadiusChange?.(radius);
        return;
      }
      emit(last, 'edited');
    };
    const onDeleted = () => {
      if (replacedDuringSession()) return;
      if (groupRef.current && groupRef.current.getLayers().length === 0) latest.current.onGeometryChange?.(null);
    };
    const onEditVertex = (e) => {
      const geom = e.poly?.toGeoJSON?.()?.geometry;
      if (geom?.type === 'LineString') setPreview(geom);
    };
    // Dragging the radius handle: the live radius goes to the draft (the popup / panel show it).
    const onEditResize = (e) => {
      const circle = editSessionRef.current?.circle;
      if (!circle || e.layer !== circle.layer) return;
      latest.current.onCircleRadiusDraft?.({ active: true, value: circle.layer.getRadius(), origin: 'layer' });
    };
    const onEditStart = () => {
      sessionGeometryRef.current = latest.current.geometry;
      setBusy(true);
    };
    const onVertexEditStart = () => {
      editSessionRef.current = {
        widthM: latest.current.corridorWidthM, saved: false, widthRequested: false, circle: null,
      };
      onEditStart();
      // EDITSTART comes before leaflet-draw enables the layers' editing; the layer is in the group already.
      const layers = groupRef.current?.getLayers() || [];
      if (layers.length !== 1 || !(layers[0] instanceof L.Circle)) return;
      const layer = layers[0];
      const circle = {
        layer, startRadiusM: layer.getRadius(), startCenter: layer.getLatLng().clone(), dragging: false,
        radiusRequested: false,
      };
      // editstart / edit = the start / end of dragging any of the circle's handles.
      circle.onDragStart = () => { circle.dragging = true; };
      circle.onDragEnd = () => {
        circle.dragging = false;
        resetRadiusHandle(layer);
      };
      layer.on('editstart', circle.onDragStart).on('edit', circle.onDragEnd);
      editSessionRef.current.circle = circle;
      setCircleSession(true);
      latest.current.onCircleRadiusDraft?.({ active: true, value: circle.startRadiusM, origin: 'layer' });
    };
    // End of a vertex edit: a cancelled (or aborted) one restores the corridor width it started with —
    // unless the area was replaced from outside meanwhile, then the session's start state is gone. A
    // circle's radius draft is dropped (Cancel reverted the circle; Save reported it).
    const endVertexEdit = () => {
      const session = editSessionRef.current;
      editSessionRef.current = null;
      if (!session) return;
      const {
        corridorWidthM: width, onCorridorWidthChange: setWidth, onCorridorWidthRequest: request,
        onCircleRadiusDraft: setDraft, onCircleRadiusRequest: requestRadiusPopup,
      } = latest.current;
      if (session.widthRequested) request?.(null);
      if (session.circle) {
        const { circle } = session;
        circle.layer.off('editstart', circle.onDragStart).off('edit', circle.onDragEnd);
        setDraft?.(CIRCLE_DRAFT_IDLE);
        if (circle.radiusRequested) requestRadiusPopup?.(null);
      }
      if (!session.saved && !replacedDuringSession() && width !== session.widthM) setWidth?.(session.widthM);
    };
    const onStop = () => {
      setPreview(null);
      setBusy(false);
      setCircleSession(false);
      setSyncTick((n) => n + 1);
    };
    const onEditStop = () => {
      endVertexEdit();
      onStop();
    };

    map.on(L.Draw.Event.DRAWSTART, onDrawStart);
    map.on(L.Draw.Event.DRAWSTOP, onStop);
    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.EDITSTART, onVertexEditStart);
    map.on(L.Draw.Event.DELETESTART, onEditStart);
    map.on(L.Draw.Event.EDITVERTEX, onEditVertex);
    map.on(L.Draw.Event.EDITRESIZE, onEditResize);
    map.on(L.Draw.Event.EDITED, onEdited);
    map.on(L.Draw.Event.DELETED, onDeleted);
    map.on(L.Draw.Event.EDITSTOP, onEditStop);
    map.on(L.Draw.Event.DELETESTOP, onStop);

    return () => {
      map.off(L.Draw.Event.DRAWSTART, onDrawStart);
      map.off(L.Draw.Event.DRAWSTOP, onStop);
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.EDITSTART, onVertexEditStart);
      map.off(L.Draw.Event.DELETESTART, onEditStart);
      map.off(L.Draw.Event.EDITVERTEX, onEditVertex);
      map.off(L.Draw.Event.EDITRESIZE, onEditResize);
      map.off(L.Draw.Event.EDITED, onEdited);
      map.off(L.Draw.Event.DELETED, onDeleted);
      map.off(L.Draw.Event.EDITSTOP, onEditStop);
      map.off(L.Draw.Event.DELETESTOP, onStop);
      control._toolbars?.draw?.disable?.();
      control._toolbars?.edit?.disable?.();
      map.removeControl(control);
      // The stop event of a session aborted here is no longer listened to — report the end ourselves
      // (the toolbar's disable() above reverted the shape; an aborted vertex edit reverts the width too).
      endVertexEdit();
      setCircleSession(false);
      if (busyRef.current) {
        setBusy(false);
        setPreview(null);
        setSyncTick((n) => n + 1);
      }
    };
  }, [map, editable, language, hasCircleTool]);

  return (
    <>
      {kind === FLIGHT_AREA_KIND.INVALID && <InvalidAreaLayer geometry={geometry} />}
      {kind === FLIGHT_AREA_KIND.CIRCLE && !(layerRadiusM > 0) && <CircleCenterLayer geometry={geometry} />}
      <CorridorLayer geometry={preview || editableGeometry} widthM={Number(corridorWidthM)} />
    </>
  );
};

export default FlightAreaLayer;
